/**
 * Création d'une démarche personnelle et modification d'une démarche.
 *
 * Pour une démarche issue de la bibliothèque, le formulaire indique clairement
 * que les champs modifiés seront protégés des mises à jour ultérieures.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { tasksApi } from "../api/endpoints";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/ui";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { centsToEuros, eurosToCents } from "../lib/format";

interface FormState {
  title: string;
  summary: string;
  description: string;
  category: string;
  authority: string;
  obligation: string;
  can_start_on: string;
  recommended_on: string;
  due_on: string;
  recurrence: string;
  every_months: string;
  procedure: string;
  consequence_of_delay: string;
  user_notes: string;
  amount_kind: string;
  amount_euros: string;
  amount_note: string;
  payment_kind: string;
  payment_due_on: string;
  manual_priority_boost: string;
}

const EMPTY: FormState = {
  title: "",
  summary: "",
  description: "",
  category: "other",
  authority: "",
  obligation: "optional",
  can_start_on: "",
  recommended_on: "",
  due_on: "",
  recurrence: "once",
  every_months: "",
  procedure: "",
  consequence_of_delay: "",
  user_notes: "",
  amount_kind: "none",
  amount_euros: "",
  amount_note: "",
  payment_kind: "",
  payment_due_on: "",
  manual_priority_boost: "0",
};

const BOOST_OPTIONS = [
  { value: "-15", label: "Moins important pour moi" },
  { value: "0", label: "Importance normale" },
  { value: "10", label: "Important pour moi" },
  { value: "20", label: "Très important pour moi" },
];

export function TaskFormPage({ mode }: { mode: "create" | "edit" }) {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  useDocumentTitle(mode === "create" ? "Ajouter une démarche" : "Modifier la démarche");

  const options = useResource(() => tasksApi.options(), []);
  const existing = useResource(
    () => tasksApi.get(taskId),
    [taskId],
    { enabled: mode === "edit" },
  );

  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    const task = existing.data;
    if (mode !== "edit" || !task) return;
    setForm({
      title: task.title,
      summary: task.summary ?? "",
      description: task.description ?? "",
      category: task.category,
      authority: task.authority ?? "",
      obligation: task.obligation,
      can_start_on: task.can_start_on ?? "",
      recommended_on: task.recommended_on ?? "",
      due_on: task.due_on ?? "",
      recurrence: task.recurrence,
      every_months: String(
        (task.recurrence_config as { every_months?: number } | null)?.every_months ?? "",
      ),
      procedure: task.procedure ?? "",
      consequence_of_delay: task.consequence_of_delay ?? "",
      user_notes: task.user_notes ?? "",
      amount_kind: task.amount_kind,
      amount_euros: centsToEuros(task.amount_cents),
      amount_note: task.amount_note ?? "",
      payment_kind: task.payment_kind ?? "",
      payment_due_on: task.payment_due_on ?? "",
      manual_priority_boost: String(task.manual_priority_boost ?? 0),
    });
  }, [existing.data, mode]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const buildPayload = (): Record<string, unknown> => {
    const amountCents = eurosToCents(form.amount_euros);
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      description: form.description.trim() || null,
      category: form.category,
      authority: form.authority.trim() || null,
      obligation: form.obligation,
      can_start_on: form.can_start_on || null,
      recommended_on: form.recommended_on || null,
      due_on: form.due_on || null,
      recurrence: form.recurrence,
      procedure: form.procedure.trim() || null,
      consequence_of_delay: form.consequence_of_delay.trim() || null,
      user_notes: form.user_notes.trim() || null,
      amount_kind: form.amount_kind,
      amount_cents: form.amount_kind === "none" ? null : amountCents,
      amount_note: form.amount_note.trim() || null,
      payment_kind: form.payment_kind || null,
      payment_due_on: form.payment_due_on || null,
      manual_priority_boost: Number(form.manual_priority_boost) || 0,
    };
    if (form.recurrence === "custom") {
      payload.recurrence_config = {
        every_months: Number(form.every_months) || 1,
      };
    }
    return payload;
  };

  const save = useMutation(
    () =>
      mode === "create"
        ? tasksApi.create(buildPayload())
        : tasksApi.update(taskId, buildPayload()),
    {
      onSuccess: (task) => {
        toast.success(
          mode === "create" ? "Démarche créée" : "Démarche mise à jour",
        );
        navigate(`/demarches/${task.id}`);
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  if (options.loading || (mode === "edit" && existing.loading)) {
    return <LoadingBlock />;
  }
  if (mode === "edit" && existing.error) {
    return <ErrorAlert error={existing.error} title="Démarche introuvable" />;
  }

  const o = options.data;
  const isSystemTask = existing.data?.source === "system_rule";
  const recurrenceNeedsDue = form.recurrence !== "once" && !form.due_on;

  return (
    <>
      <PageHeader
        title={mode === "create" ? "Ajouter une démarche" : "Modifier la démarche"}
        subtitle={
          mode === "create"
            ? "Vos démarches personnelles sont clairement distinctes des obligations issues de la bibliothèque administrative."
            : undefined
        }
        actions={
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Annuler
          </Button>
        }
      />

      {isSystemTask && (
        <Alert tone="info" title="Démarche issue de la bibliothèque administrative">
          Les champs que vous modifiez ici sont conservés : une mise à jour de la
          bibliothèque ne les écrasera pas. Les autres champs continueront d'être
          tenus à jour depuis la source officielle.
        </Alert>
      )}

      <ErrorAlert error={save.error} title="Enregistrement refusé" />

      <form
        className="stack mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save.run();
        }}
        noValidate
      >
        <Card title="Description">
          <TextField
            label="Titre"
            required
            autoFocus={mode === "create"}
            value={form.title}
            onChange={(event) => set("title", event.target.value)}
            error={save.fieldErrors.title}
            hint="Exemple : Renouveler mon nom de domaine"
          />
          <TextField
            label="Résumé"
            optional
            value={form.summary}
            onChange={(event) => set("summary", event.target.value)}
            error={save.fieldErrors.summary}
          />
          <TextAreaField
            label="Description"
            optional
            value={form.description}
            onChange={(event) => set("description", event.target.value)}
            error={save.fieldErrors.description}
          />
          {o && (
            <div className="grid grid--2">
              <SelectField
                label="Catégorie"
                options={o.categories}
                value={form.category}
                onChange={(event) => set("category", event.target.value)}
              />
              <SelectField
                label="Caractère"
                options={o.obligations}
                value={form.obligation}
                onChange={(event) => set("obligation", event.target.value)}
                hint="Une démarche facultative ne passera pas devant une obligation légale."
              />
            </div>
          )}
          <TextField
            label="Organisme concerné"
            optional
            value={form.authority}
            onChange={(event) => set("authority", event.target.value)}
            hint="Exemple : Urssaf, service des impôts, assureur, registrar."
          />
        </Card>

        <Card title="Dates et périodicité">
          <div className="grid grid--3">
            <TextField
              label="Réalisable à partir du"
              type="date"
              optional
              value={form.can_start_on}
              onChange={(event) => set("can_start_on", event.target.value)}
              error={save.fieldErrors.can_start_on}
            />
            <TextField
              label="Date conseillée"
              type="date"
              optional
              value={form.recommended_on}
              onChange={(event) => set("recommended_on", event.target.value)}
              error={save.fieldErrors.recommended_on}
            />
            <TextField
              label="Date limite"
              type="date"
              optional
              value={form.due_on}
              onChange={(event) => set("due_on", event.target.value)}
              error={save.fieldErrors.due_on}
            />
          </div>
          {o && (
            <SelectField
              label="Périodicité"
              options={o.recurrences}
              value={form.recurrence}
              onChange={(event) => set("recurrence", event.target.value)}
              hint="Une démarche récurrente crée automatiquement l'occurrence suivante dès que vous la validez."
              error={
                recurrenceNeedsDue
                  ? "Renseignez une date limite pour une démarche récurrente."
                  : save.fieldErrors.recurrence
              }
            />
          )}
          {form.recurrence === "custom" && (
            <TextField
              label="Tous les combien de mois ?"
              type="number"
              min={1}
              max={120}
              value={form.every_months}
              onChange={(event) => set("every_months", event.target.value)}
            />
          )}
        </Card>

        <Card title="Paiement à anticiper" hint="Facultatif.">
          {o && (
            <SelectField
              label="Nature du montant"
              options={o.amount_kinds}
              value={form.amount_kind}
              onChange={(event) => set("amount_kind", event.target.value)}
            />
          )}
          {form.amount_kind !== "none" && (
            <>
              <TextField
                label="Montant en euros"
                inputMode="decimal"
                optional={form.amount_kind !== "known"}
                value={form.amount_euros}
                onChange={(event) => set("amount_euros", event.target.value)}
                error={save.fieldErrors.amount_cents}
                hint="Exemple : 15,00"
              />
              {o && (
                <SelectField
                  label="Type de paiement"
                  options={o.payment_kinds}
                  placeholder="Non précisé"
                  value={form.payment_kind}
                  onChange={(event) => set("payment_kind", event.target.value)}
                />
              )}
              <TextField
                label="Date prévisionnelle de paiement"
                type="date"
                optional
                value={form.payment_due_on}
                onChange={(event) => set("payment_due_on", event.target.value)}
              />
              <TextAreaField
                label="Commentaire sur le calcul"
                optional
                value={form.amount_note}
                onChange={(event) => set("amount_note", event.target.value)}
              />
            </>
          )}
        </Card>

        <Card title="Aide-mémoire et importance">
          <TextAreaField
            label="Procédure à suivre"
            optional
            value={form.procedure}
            onChange={(event) => set("procedure", event.target.value)}
          />
          <TextAreaField
            label="Conséquence d'un retard"
            optional
            value={form.consequence_of_delay}
            onChange={(event) => set("consequence_of_delay", event.target.value)}
          />
          <TextAreaField
            label="Mes notes"
            optional
            value={form.user_notes}
            onChange={(event) => set("user_notes", event.target.value)}
          />
          <SelectField
            label="Importance pour moi"
            options={BOOST_OPTIONS}
            value={form.manual_priority_boost}
            onChange={(event) => set("manual_priority_boost", event.target.value)}
            hint="Ajuste le score de priorité calculé, sans écraser le caractère obligatoire."
          />
        </Card>

        <div className="btn-group">
          <Button
            type="submit"
            variant="primary"
            loading={save.pending}
            disabled={!form.title.trim() || recurrenceNeedsDue}
          >
            {mode === "create" ? "Créer la démarche" : "Enregistrer les modifications"}
          </Button>
          <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
            Annuler
          </Button>
        </div>
      </form>
    </>
  );
}
